'use client';

import { useEffect, useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import Flow from './Flow';
import { EntityListResp, SupportStatus } from '@/types/home';
import { AnalysisDetail } from '@/containers/home/analysis/AnalysisDetail';
export type EntityListProps = {
  entityLoading: boolean;
  entityList: EntityListResp[];
};
import { ReactFlowProvider } from '@xyflow/react';
import Bubble from './Bubble';
import { useFm } from '@web3/base-hooks';

// 获取状态的优先级权重
const getStatusPriority = (status: SupportStatus): number => {
  switch (status) {
    case SupportStatus.ALL_LOST:
      return 2;
    case SupportStatus.PENDING_INFO:
      return 1;
    default:
      return 0;
  }
};

const Analysis = (props: EntityListProps) => {
  const { entityList = [], entityLoading } = props;
  const fm = useFm();

  // 先按照 supportStatus 优先级排序，再按照 unblocked 金额降序排序
  const sortedList = useMemo(
    () =>
      [...entityList].sort((a, b) => {
        const priorityA = getStatusPriority(a.supportStatus);
        const priorityB = getStatusPriority(b.supportStatus);

        // 如果优先级不同，按优先级排序
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }

        // 如果优先级相同，则按照 unblocked 金额降序排序
        return Number(b.unblocked) - Number(a.unblocked);
      }),
    [entityList]
  );

  const [curEntity, setCurEntity] = useState<EntityListResp | null>(null);

  const onNodeClick = (entity: EntityListResp) => {
    setCurEntity(entity);
  };

  useEffect(() => {
    if (sortedList.length === 0) return;
    setCurEntity(sortedList[0]);
  }, [sortedList]);

  return (
    <>
      <div className="max-w-[1200px] mx-auto h-[720px] flex justify-between md:mt-[64px]">
        <Tabs defaultValue="hacker" className="w-full flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div
              className="text-[#FFF] text-[40px] font-[600] leading-[48px] normal-case"
              style={{
                fontFamily:
                  '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontStyle: 'normal',
              }}
            >
              Analysis
            </div>
            <div className="flex items-center">
              <TabsList className="grid w-full grid-cols-2 bg-[#25282C] rounded-[8px] h-[56px]">
                <TabsTrigger
                  className="data-[state=active]:bg-[#333537] h-[48px] rounded-[8px] font-ibm-plex-sans text-sm font-medium"
                  value="hacker"
                >
                  {fm('hackerAddresses')}
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-[#333537] h-[48px] rounded-[8px] font-ibm-plex-sans text-sm font-medium"
                  value={fm('destinations')}
                >
                  {fm('destinations')}
                </TabsTrigger>
              </TabsList>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <TabsContent value="hacker" className="h-full">
              <Bubble />
            </TabsContent>
            <TabsContent value={fm('destinations')} className="h-full">
              {entityLoading ? null : (
                <div className="flex w-full h-full bg-[#101014]">
                  <div className="flex w-2/3 h-full">
                    <div className="w-full h-full">
                      <ReactFlowProvider>
                        <Flow
                          onNodeClick={onNodeClick}
                          entityList={sortedList}
                          curEntity={curEntity}
                        />
                      </ReactFlowProvider>
                    </div>
                  </div>
                  <div className="flex px-16 items-center h-full overflow-auto">
                    <AnalysisDetail curEntity={curEntity} />
                  </div>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
};

export default Analysis;
